import { useState, useEffect, useRef, useCallback } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Draggable from 'react-draggable';
import { API, Auth } from 'aws-amplify';

// Context and utilities
import { useAppContext } from '../../Lib/contextLib';
import { getVideo, GetUserVideo } from '../../s3Func/s3Download';

// Components
import PopUp from '../../Popup/logoutPopup';
import { CloudfrontVideoFormat } from '../../videos/CloudfrontVideoFormat';
import Learn from '../../Learn/Learn';
import Practice from './Practice';
import SingingTime from '../../SingingTime/SingingTime';
import Celebrate from '../../Celebrate/Celebrate';
import Surprise from '../../SurpriseVideo/surprise';
import FollowTheBeatModule9 from '../../FollowTheBeat/FollowTheBeatModule9.js';

// Assets
import presentSound from '../../Unit1/Module1/assets/ta_da_gift.mp3';
import loadingGif from '../../loading-gif-png-5.gif';

// Styles
import './Module14World.css';

// ============================================================================
// Constants
// ============================================================================

const AFTER_CELEBRATE_VIDEO_DAY9 = 'appfiles/solutions/closing.mp4';
const MODULE_NAME = 'MODULE14';
const NAVIGATION_PATH = '/solutions/navigation';
const SPECIAL_DAYS = { DAY_4: 4, DAY_8: 8 };
const COIN_PRIZE_THRESHOLD = 100;

// Step completion states
const STEPS = {
  NOT_STARTED: 'notstarted',
  LEARN: 'learn',
  PRACTICE: 'practice',
  FOLLOW_THE_BEAT: 'followthebeat',
  CELEBRATE: 'celebrate',
  SINGING_TIME: 'singingtime',
  SURPRISE: 'surprise',
};

// Grade types that bypass certain restrictions
const UNRESTRICTED_GRADES = ['Teacher Mode', 'Free Play'];

// ============================================================================
// Utility Functions
// ============================================================================

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isIOSDevice = () => {
  const iosDevices = [
    'iPad Simulator',
    'iPhone Simulator',
    'iPod Simulator',
    'iPad',
    'iPhone',
    'iPod',
  ];
  return (
    iosDevices.includes(navigator.platform) ||
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  );
};

const isUnrestrictedGrade = (grade) => UNRESTRICTED_GRADES.includes(grade);

// ============================================================================
// Custom Hooks
// ============================================================================

const useWindowDimensions = () => {
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return dimensions;
};

// ============================================================================
// Main Component
// ============================================================================

const Module14World = () => {
  const { width: clientWidth, height: clientHeight } = useWindowDimensions();
  const { userHasAuthenticated } = useAppContext();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state
  const { module14 } = useSelector((state) => state.module14, shallowEqual);
  const { login } = useSelector((state) => state.loginReducer, shallowEqual);

  // Refs for tracking initial renders
  const firstUpdate = useRef(true);
  const coinUpdate = useRef(true);

  // ------------------------------------------------------------------------
  // Image URL Generator
  // ------------------------------------------------------------------------
  const generateImageUrl = (baseUrl) => login.cloudFront + baseUrl + login.cookie;

  // ------------------------------------------------------------------------
  // Image Assets
  // ------------------------------------------------------------------------
  const images = {
    // Backgrounds
    background: generateImageUrl('images/Module14Images/Background14-01.png'),
    backgroundWithoutTrees: generateImageUrl('images/Module14Images/landscape_without_trees.png'),
    tree: generateImageUrl('images/Module14Images/U3m4.png'),

    // Character
    monkeyStill: generateImageUrl('images/Module4Images/Monkey+Practice+still.gif'),

    // UI Elements
    coinPick: generateImageUrl('images/Images/AwesomeCoin.png'),
    coinBar: generateImageUrl('images/Images/Coin+Counter.png'),
    journeyMap: generateImageUrl('images/Module1Images/bluejourneymap.svg'),
    greenCheck: generateImageUrl('images/Module1Images/GreenCheck-Button.png'),
    present: generateImageUrl('images/Images/Gift.png'),
    keyReward: generateImageUrl('images/Module4Images/Reward_Banana.png'),

    // Buttons
    logoutBtn: generateImageUrl('images/Module1Images/Logout.png'),
    reviewBtn: generateImageUrl('images/Module1Images/Button_Review.png'),
    rewardBtn: generateImageUrl('images/Module1Images/Button_Reward_3x.png'),
    celebrateBtn: generateImageUrl('images/Module1Images/Button_Celebrate.png'),
    learnBtn: generateImageUrl('images/Module1Images/Button_Learn.png'),
    practiceBtn: generateImageUrl('images/Module1Images/Button_Practice.png'),
    ftbBtn: generateImageUrl('images/Images/Button_FTB.png'),
    singingTimeBtn: generateImageUrl('images/Module1Images/Button_SingingTime.png'),

    // Progress bars (banana)
    progressBars: [
      generateImageUrl('images/Module4Images/ProgressBanana0.png'),
      generateImageUrl('images/Module4Images/ProgressBanana1.png'),
      generateImageUrl('images/Module4Images/ProgressBanana2.png'),
      generateImageUrl('images/Module4Images/ProgressBanana2.png'),
      generateImageUrl('images/Module4Images/Reward_Banana.png'),
    ],

    // Rewards
    rewards: {
      Elephants: generateImageUrl('images/Module4Images/Reward_Elephant.png'),
      Zebras: generateImageUrl('images/Module4Images/Reward_Zebras.png'),
      Yeti: generateImageUrl('images/Module9Images/DailyReward_Yeti.png'),
      YetiBrown: generateImageUrl('images/Module9Images/DailyReward_Yeti_Brown.png'),
      JeepBlue: generateImageUrl('images/Module4Images/Reward_Car2.png'),
      JeepPink: generateImageUrl('images/Module4Images/Reward_Car.png'),
      Dragon: generateImageUrl('images/Module9Images/DailyReward_Dragon.png'),
      Pheonix: generateImageUrl('images/Module9Images/DailyReward_Pheonix.png'),
      Parrot: generateImageUrl('images/Module4Images/Reward_BirdColors.png'),
      Tucan: generateImageUrl('images/Module4Images/Reward_Tucan.png'),
      Horse1: generateImageUrl('images/Module14Images/UnicornBlue.png'),
      Horse2: generateImageUrl('images/Module14Images/UnicornPink.png'),
      Lion: generateImageUrl('images/Module4Images/Reward_Lion.png'),
      Cheetah: generateImageUrl('images/Module4Images/Reward_Cheetah.png'),
      Hat1: generateImageUrl('images/Module9Images/DailyReward_Hat1.png'),
      Hat2: generateImageUrl('images/Module9Images/DailyReward_Hat2.png'),
      MedievalCastle: generateImageUrl('images/Module9Images/BigReward_Castle.png'),
      StoneSword: generateImageUrl('images/Module9Images/BigReward_StoneSword.png'),
    },
  };

  // Progress bar arrays for different day types
  const progressBar5Keys = [
    images.progressBars[0],
    images.progressBars[1],
    images.progressBars[2],
    images.progressBars[2],
    images.progressBars[3],
    images.progressBars[4],
  ];
  const progressBar4Keys = images.progressBars;

  // Reward options by day
  const rewardOptionsByDay = {
    1: [images.rewards.Elephants, images.rewards.Zebras],
    2: [images.rewards.Yeti, images.rewards.YetiBrown],
    3: [images.rewards.JeepBlue, images.rewards.JeepPink],
    4: [images.rewards.Dragon, images.rewards.Pheonix],
    5: [images.rewards.Tucan, images.rewards.Parrot],
    6: [images.rewards.Horse1, images.rewards.Horse2],
    7: [images.rewards.Lion, images.rewards.Cheetah],
    8: [images.rewards.Hat1, images.rewards.Hat2],
  };

  // Influencers array
  const influencers = [
    login.influencerid,
    login.influencerid1,
    login.influencerid2,
    login.influencerid3,
    login.influencerid4,
  ];

  // Audio for touch interactions
  const touchSound = new Audio(generateImageUrl('media/Module1Videos/touch_button.mp3'));

  // ------------------------------------------------------------------------
  // UI State
  // ------------------------------------------------------------------------
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [animal, setAnimal] = useState(images.monkeyStill);

  // Icon visibility state
  const [reviewIconVisible, setReviewIconVisible] = useState(false);
  const [keyIcon, setKeyIcon] = useState(false);
  const [coinIcon, setCoinIcon] = useState(false);
  const [presentIcon, setPresentIcon] = useState(false);

  // Reward selection state
  const [rewardOption1, setRewardOption1] = useState(null);
  const [rewardOption2, setRewardOption2] = useState(null);
  const [displayReward, setDisplayReward] = useState(false);
  const [displayGreenCheck, setDisplayGreenCheck] = useState(false);
  const [dropZone, setDropZone] = useState(false);
  const [displayGreenButton, setDisplayGreenButton] = useState(false);

  // Screen/component visibility state
  const [displayLearn, setDisplayLearn] = useState(false);
  const [displayPractice, setDisplayPractice] = useState(false);
  const [displayDay4Practice, setDisplayDay4Practice] = useState(false);
  const [displaySingingTime, setDisplaySingingTime] = useState(false);
  const [displayCelebrate, setDisplayCelebrate] = useState(false);
  const [displayBody, setDisplayBody] = useState(true);
  const [displayIntroVideo, setDisplayIntroVideo] = useState(false);
  const [displaySurprise, setDisplaySurprise] = useState(false);
  const [displayVoice, setDisplayVoice] = useState(false);
  const [displayFTB, setDisplayFTB] = useState(false);

  // Component content state
  const [learnComponent, setLearnComponent] = useState('');
  const [day4PracticeComponent, setDay4PracticeComponent] = useState('');
  const [singTimeComponent, setSingTimeComponent] = useState('');
  const [celebrateComponent, setCelebrateComponent] = useState('');
  const [introVideo, setIntroVideo] = useState('');
  const [surpriseVideo, setSurpriseVideo] = useState('');

  // Animal/Monkey state
  const [showAnimal, setShowAnimal] = useState(false);
  const [animalState, setAnimalState] = useState('');
  const [animalVoice, setAnimalVoice] = useState('');
  const [playAnimalClose, setPlayAnimalClose] = useState(false);

  // Prize state
  const [prize, setPrize] = useState(false);
  const [displayPrize, setDisplayPrize] = useState(false);

  // Animation state
  const [keyAnimationState, setKeyAnimationState] = useState('bounceIcons');
  const [coinAnimationState, setCoinAnimationState] = useState('bounceIcons');

  // Audio state
  const [touchButton, setTouchButton] = useState(false);
  const [checkSound, setCheckSound] = useState(false);
  const [buttonSound, setButtonSound] = useState(false);
  const [rewardsFound, setRewardsFound] = useState(false);
  const [coinAudio, setCoinAudio] = useState(false);
  const [audioReady, setAudioReady] = useState(false);

  // Tracking state
  const [checkStep, setCheckStep] = useState('');
  const [showReward, setShowReward] = useState(false);

  // Follow The Beat (FTB) assets
  const [ftbMusic, setFTBMusic] = useState('');
  const [ftbInstruction, setFTBInstruction] = useState('');
  const [ftbInstructionGIF, setFTBInstructionGIF] = useState('');
  const [ftbCount, setFTBCount] = useState('');
  const [ftbTutorial1, setFTBTutorial1] = useState('');
  const [ftbTutorial2, setFTBTutorial2] = useState('');
  const [ftbWalkthrough, setFTBWalkthrough] = useState('');

  // GIF and audio assets
  const [monkeyEat, setMonkeyEat] = useState('');
  const [monkeyEatAudio, setMonkeyEatAudio] = useState('');
  const [specialMessage, setSpecialMessage] = useState('');
  const [watchConversation, setWatchConversation] = useState('');
  const [amazingJob, setAmazingJob] = useState('');
  const [practiceIntro, setPracticeIntro] = useState('');
  const [learnIntro, setLearnIntro] = useState('');
  const [ftbIntro, setFtbIntro] = useState('');
  const [celebrateIntro, setCelebrateIntro] = useState('');
  const [animalClose, setAnimalClose] = useState('');
  const [animalCloseAudio, setAnimalCloseAudio] = useState('');

  // ------------------------------------------------------------------------
  // Audio Helper
  // ------------------------------------------------------------------------

  const playButtonAudio = useCallback(() => {
    sleep(700).then(() => {
      setButtonSound(true);
    });
  }, []);

  // ------------------------------------------------------------------------
  // Asset Loading
  // ------------------------------------------------------------------------

  const loadGifAssets = useCallback(() => {
    // Monkey GIF assets
    setMonkeyEat(getVideo('appfiles/posture/monkey_banana.gif', login));
    setMonkeyEatAudio(getVideo('appfiles/posture/monkey_eating.mp3', login));

    // Practice and intro GIFs
    setSpecialMessage(getVideo('appfiles/respond/respond_special_message.gif', login));
    setWatchConversation(getVideo('appfiles/respond/respond_watch_conversation.gif', login));
    setAmazingJob(getVideo('appfiles/respond/respond_amazing_job.gif', login));
    setPracticeIntro(getVideo('appfiles/respond/respond_practice_intro.gif', login));
    setLearnIntro(getVideo('appfiles/respond/respond_learn_intro.gif', login));
    setFtbIntro(getVideo('appfiles/respond/respond_ftb_intro.gif', login));
    setCelebrateIntro(getVideo('appfiles/respond/respond_celebrate_intro.gif', login));
    setAnimalClose(getVideo('appfiles/respond/respond_day_finished.gif', login));
    setFTBInstructionGIF(getVideo('appfiles/respond/respond_ftb_instructions.gif', login));
    setFTBCount(getVideo('appfiles/respond/countingMonkey.gif', login));

    // Audio assets
    setAnimalCloseAudio(getVideo('appfiles/solutions/solutions_day_finished.mp3', login));
    setFTBInstruction(getVideo('appfiles/solutions/solutions_ftb_instructions.mp3', login));
    setFTBMusic(getVideo('appfiles/respond/respond_ftb_audio.m4a', login));

    // Tutorial assets (only for first-time users)
    if (!module14.unlockedDays.day2) {
      setFTBTutorial1(getVideo('appfiles/greet/ftb_walkthrough_touch_circle.mp3', login));
      setFTBTutorial2(getVideo('appfiles/solutions/ftb_walkthrough_tap.mp3', login));
      setFTBWalkthrough(getVideo('appfiles/respond/ftb_walkthrough_audio.mp3', login));
    }
  }, [login, module14.unlockedDays.day2]);

  useEffect(() => {
    loadGifAssets();
  }, [loadGifAssets]);

  // ------------------------------------------------------------------------
  // Game State Management
  // ------------------------------------------------------------------------

  const createGameDetails = useCallback(
    (flag, reward, coin) => {
      const gameDetail = { ...module14.game_details };

      if (flag) {
        const flagEntry = { flag, lastUpdated: new Date().toJSON() };
        gameDetail.moduleFlags.push(flagEntry);
      }

      if (coin >= 0) {
        gameDetail.tokens_earned = coin;
      }

      if (reward) {
        const dailyRewards = Object.entries(module14.rewards)
          .filter(([, rewardData]) => rewardData.unlocked)
          .map(([key, rewardData]) => ({
            type: key,
            x_multiplier: rewardData.x,
            y_multiplier: rewardData.y,
          }));
        gameDetail.dailyRewards = dailyRewards;
      }

      dispatch({ type: 'MODULE14_SET_GameDetials', payload: gameDetail });
    },
    [dispatch, module14.game_details, module14.rewards]
  );

  // ------------------------------------------------------------------------
  // API Calls
  // ------------------------------------------------------------------------

  const updateProgress = async () => {
    const payload = {
      body: {
        id: login.studentid,
        module: 'Solutions',
        day_completed: module14.currentday,
        time_completed: new Date().toDateString(),
      },
    };

    try {
      await API.put('TIR', '/v3/student/progress', payload);
    } catch (error) {
      // Silent fail - progress update is non-critical
    }
  };

  const updateDB = async () => {
    const modulePayload = {
      body: {
        id: module14.moduleID,
        day_completed: module14.day_completed,
        step_completed: module14.stepCompleted,
        game_details: JSON.stringify(module14.game_details),
      },
    };

    try {
      await API.put('TIR', '/v3/student/module', modulePayload);

      if (displayGreenButton) {
        setAnimalVoice(null);
        setShowAnimal(true);
        setPlayAnimalClose(true);
        setAnimalState(animalClose);
        setDisplayGreenButton(false);
        setDisplayReward(false);
      }
    } catch (error) {
      const criticalSteps = [STEPS.NOT_STARTED, STEPS.LEARN, STEPS.PRACTICE, STEPS.CELEBRATE, STEPS.FOLLOW_THE_BEAT];
      if (criticalSteps.includes(module14.stepCompleted)) {
        window.alert('Connection is lost. Please login again.');
        dispatch({ type: 'CLEAR' });
        Auth.signOut();
        navigate('/');
      }
    }

    // Update current module for student
    const studentPayload = {
      body: {
        id: login.studentid,
        current_module: 'Solutions',
      },
    };

    try {
      await API.put('TIR', '/v3/student', studentPayload);
    } catch (error) {
      // Silent fail - student update is non-critical
    }
  };

  // ------------------------------------------------------------------------
  // Effects for State Synchronization
  // ------------------------------------------------------------------------

  // Sync step completion to database
  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }

    if (!isUnrestrictedGrade(login.grade)) {
      updateDB();
    }
  }, [module14.stepCompleted]);

  // Handle section completion
  useEffect(() => {
    const shouldUpdateProgress =
      (module14.reviewIcon || login.grade === 'Teacher Mode') &&
      module14.sectionCompleted === 4;

    if (shouldUpdateProgress) {
      updateProgress();
      module14.sectionCompleted = 0;
    }
  }, [module14.sectionCompleted]);

  // Handle coin updates and prize eligibility
  useEffect(() => {
    if (module14.coins >= COIN_PRIZE_THRESHOLD) {
      setPrize(true);
    }

    if (coinUpdate.current) {
      coinUpdate.current = false;
      return;
    }

    createGameDetails(false, false, module14.coins);
    updateDB();
  }, [module14.coins]);

  // ------------------------------------------------------------------------
  // UI Handlers
  // ------------------------------------------------------------------------

  const togglePopup = () => {
    setIsPopupOpen((prev) => !prev);
  };

  // Handle progress key updates based on step completion
  useEffect(() => {
    const isDayCompleted = module14.currentday <= module14.day_completed;
    const hasUnrestrictedAccess = isUnrestrictedGrade(login.grade);

    if (isDayCompleted || hasUnrestrictedAccess) {
      // Day is completed or user has unrestricted access
      if (hasUnrestrictedAccess) {
        setShowReward(true);
      }

      dispatch({ type: 'MODULE14_TOGGLE_REVIEW_ICON', payload: true });

      const isSpecialDay = module14.currentday === SPECIAL_DAYS.DAY_4 || module14.currentday === SPECIAL_DAYS.DAY_8;
      dispatch({ type: 'MODULE14_SET_KEY', payload: isSpecialDay ? 5 : 4 });
    } else {
      // Day is in progress
      if (module14.stepCompleted === checkStep) {
        setCoinIcon(true);
      }

      dispatch({ type: 'MODULE14_TOGGLE_REVIEW_ICON', payload: false });

      const isDay4Start = module14.currentday === SPECIAL_DAYS.DAY_4 && module14.day_completed === 3;

      if (isDay4Start) {
        // Day 4 has a different step sequence (includes singing time first)
        const day4KeyMap = {
          [STEPS.NOT_STARTED]: 0,
          [STEPS.SINGING_TIME]: 1,
          [STEPS.LEARN]: 2,
          [STEPS.PRACTICE]: 3,
          [STEPS.FOLLOW_THE_BEAT]: 4,
          [STEPS.CELEBRATE]: 5,
        };

        const keyValue = day4KeyMap[module14.stepCompleted];
        if (keyValue !== undefined) {
          dispatch({ type: 'MODULE14_SET_KEY', payload: keyValue });
          if (module14.stepCompleted === STEPS.CELEBRATE) {
            setKeyIcon(true);
          }
        }
      } else {
        // Standard day progression
        const standardKeyMap = {
          [STEPS.NOT_STARTED]: 0,
          [STEPS.LEARN]: 1,
          [STEPS.PRACTICE]: 2,
          [STEPS.FOLLOW_THE_BEAT]: 3,
          [STEPS.CELEBRATE]: 4,
          [STEPS.SURPRISE]: 5,
        };

        const keyValue = standardKeyMap[module14.stepCompleted];
        if (keyValue !== undefined) {
          dispatch({ type: 'MODULE14_SET_KEY', payload: keyValue });

          // Handle special cases for celebrate step
          if (module14.stepCompleted === STEPS.CELEBRATE) {
            const isDay8Surprise = module14.currentday === SPECIAL_DAYS.DAY_8 && module14.day_completed === 7;
            if (isDay8Surprise) {
              setPresentIcon(true);
            } else {
              setKeyIcon(true);
            }
          } else if (module14.stepCompleted === STEPS.SURPRISE) {
            setKeyIcon(true);
          }
        }
      }

      playButtonAudio();
    }
  }, [module14.stepCompleted]);

  // ------------------------------------------------------------------------
  // Reward Selection Handlers
  // ------------------------------------------------------------------------

  const handleRewardSelection = (selectedOption) => {
    const isOption1 = selectedOption === 1;
    const day = module14.currentday;

    // Update Redux state
    dispatch({ type: `MODULE14_SET_REWARD${day}1`, payload: isOption1 });
    dispatch({ type: `MODULE14_SET_REWARD${day}2`, payload: !isOption1 });

    // Update visual selection feedback
    document.getElementById('rewardItemLeft2').style.borderColor = isOption1 ? 'yellow' : 'white';
    document.getElementById('rewardItemRight2').style.borderColor = isOption1 ? 'white' : 'yellow';

    setTouchButton(true);
    setDisplayGreenCheck(true);
  };

  const handleRewardConfirmation = () => {
    document.getElementById('rewardList').style.zIndex = 1;
    setCheckSound(true);
    setDropZone(true);
    setDisplayGreenCheck(false);
    setDisplayGreenButton(true);
  };

  const handleDayCompletion = () => {
    setCheckSound(true);
    setDropZone(false);
    updateProgress();

    sleep(500).then(() => {
      createGameDetails(false, true);

      if (isUnrestrictedGrade(login.grade)) {
        updateDB();
      }

      const nextDay = module14.currentday + 1;
      const completedDay = module14.currentday === SPECIAL_DAYS.DAY_8 ? 9 : module14.currentday;

      dispatch({ type: `MODULE14_SET_DAY_UNLOCK${nextDay}`, payload: true });
      dispatch({ type: 'MODULE14_SET_dayCompleted', payload: completedDay });
      dispatch({ type: 'MODULE14_SET_stepCompleted', payload: STEPS.NOT_STARTED });
    });
  };

  const renderRewardSelection = () => (
    <>
      <div className="rewards" id="rewardList">
        <div className="circle" id="rewardItemLeft2">
          <img
            src={rewardOption1}
            alt="Reward option 1"
            onClick={() => handleRewardSelection(1)}
          />
        </div>
        <div className="circle" id="rewardItemRight2">
          <img
            src={rewardOption2}
            alt="Reward option 2"
            onClick={() => handleRewardSelection(2)}
          />
        </div>
      </div>

      <div className="greenCheckDiv">
        {displayGreenCheck && (
          <img
            className="greenCheck"
            src={images.greenCheck}
            alt="Confirm selection"
            onClick={handleRewardConfirmation}
          />
        )}
      </div>

      {dropZone && (
        <div className="area">
          <div id="dropZone">
            <div id="word">Drag and drop your item</div>
          </div>
        </div>
      )}

      <div className="greenCheckDiv">
        {displayGreenButton && (
          <img
            className="greenCheck"
            src={images.greenCheck}
            alt="Complete day"
            onClick={handleDayCompletion}
          />
        )}
      </div>
    </>
  );

  // ------------------------------------------------------------------------
  // Prize Selection Handlers
  // ------------------------------------------------------------------------

  const handlePrizeSelection = (selectedOption) => {
    const isOption1 = selectedOption === 1;

    dispatch({ type: 'MODULE14_SET_PRIZE1', payload: isOption1 });
    dispatch({ type: 'MODULE14_SET_PRIZE2', payload: !isOption1 });

    document.getElementById('rewardItemLeft').style.borderColor = isOption1 ? 'yellow' : 'white';
    document.getElementById('rewardItemRight').style.borderColor = isOption1 ? 'white' : 'yellow';

    setTouchButton(true);
    setDisplayGreenCheck(true);
  };

  const handlePrizeConfirmation = () => {
    setDisplayPrize(false);
    setCheckSound(true);
    setDropZone(false);

    sleep(500).then(() => {
      setDisplayGreenButton(false);
      createGameDetails(false, true);
      dispatch({ type: 'MODULE14_SET_COIN', payload: module14.coins - COIN_PRIZE_THRESHOLD });
    });
  };

  const renderPrizeSelection = () => (
    <>
      <div className="rewards">
        <div className="circle" id="rewardItemLeft">
          <img
            src={images.rewards.MedievalCastle}
            alt="Castle prize"
            onClick={() => handlePrizeSelection(1)}
          />
        </div>
        <div className="circle" id="rewardItemRight">
          <img
            src={images.rewards.StoneSword}
            alt="Stone Sword prize"
            onClick={() => handlePrizeSelection(2)}
          />
        </div>
      </div>

      <div className="greenCheckDiv">
        {displayGreenCheck && (
          <img
            className="greenCheck"
            src={images.greenCheck}
            alt="Confirm selection"
            onClick={() => {
              setCheckSound(true);
              setDropZone(true);
              setDisplayGreenCheck(false);
              setDisplayGreenButton(true);
            }}
          />
        )}
      </div>

      {dropZone && (
        <div className="area">
          <div id="dropZone">
            <div id="word">Drag and drop your item</div>
          </div>
        </div>
      )}

      <div className="greenCheckDiv">
        {displayGreenButton && (
          <img
            className="greenCheck"
            src={images.greenCheck}
            alt="Confirm prize"
            onClick={handlePrizeConfirmation}
          />
        )}
      </div>
    </>
  );

  // Set reward options based on current day
  useEffect(() => {
    const options = rewardOptionsByDay[module14.currentday];

    if (options) {
      setRewardOption1(options[0]);
      setRewardOption2(options[1]);
    } else {
      setDisplayGreenButton(true);
    }
  }, [keyIcon, module14.currentday]);

  // ------------------------------------------------------------------------
  // Review Mode Rendering
  // ------------------------------------------------------------------------

  const renderReviewButtons = () => {
    const isDay4 = module14.currentday === SPECIAL_DAYS.DAY_4;

    if (isDay4) {
      // Day 4 includes singing time button
      return (
        <div className="world2reviewContainer">
          <img
            className="reviewBtns41"
            src={images.singingTimeBtn}
            alt="singing button"
            onClick={displaySinging}
          />
          <img
            className="reviewBtns42"
            src={images.learnBtn}
            alt="learn button"
            onClick={openLearn}
          />
          <img
            className="reviewBtns43"
            src={images.practiceBtn}
            alt="practice button"
            onClick={openPractice}
          />
          <img
            className="reviewBtns44"
            src={images.ftbBtn}
            alt="ftb button"
            onClick={openFTB}
          />
          <img
            className="reviewBtns45"
            src={images.celebrateBtn}
            alt="celebrate button"
            onClick={openCelebrate}
          />
        </div>
      );
    }

    // Standard review buttons
    return (
      <div className="world2reviewContainer">
        <img
          className="reviewBtns1"
          src={images.learnBtn}
          alt="learn button"
          onClick={openLearn}
        />
        <img
          className="reviewBtns2"
          src={images.practiceBtn}
          alt="practice button"
          onClick={openPractice}
        />
        <img
          className="reviewBtns3"
          src={images.ftbBtn}
          alt="ftb button"
          onClick={openFTB}
        />
        <img
          className="reviewBtns4"
          src={images.celebrateBtn}
          alt="celebrate button"
          onClick={openCelebrate}
        />
      </div>
    );
  };

  // ------------------------------------------------------------------------
  // Video and Activity Handlers
  // ------------------------------------------------------------------------

  const handleIntroVideoEnd = () => {
    setDisplayIntroVideo(false);
    setDisplayBody(true);

    if (module14.currentday === SPECIAL_DAYS.DAY_4) {
      dispatch({ type: 'MODULE14_SET_KEY', payload: 1 });
      playButtonAudio();
    }

    if (module14.day_completed === 9) {
      navigate(NAVIGATION_PATH);
    }
  };

  const renderIntroduction = () => (
    <div>
      <CloudfrontVideoFormat
        source={introVideo}
        back={NAVIGATION_PATH}
        background={images.background}
        token={login.cookie}
        ended={handleIntroVideoEnd}
      />
    </div>
  );

  // Learn video configuration by day
  const learnVideosByDay = {
    1: { video: 'appfiles/solutions/learn_day_1.mp4', closingVideo: 'appfiles/solutions/learn_day_1_closing.mp4' },
    2: { video: 'appfiles/solutions/learn_day_2.mp4', closingVideo: 'appfiles/solutions/learn_day_2_closing.mp4' },
    3: { video: 'appfiles/solutions/learn_day_3.mp4', closingVideo: 'appfiles/solutions/learn_day_3_closing.mp4' },
  };

  const openLearn = async () => {
    const song = getVideo('appfiles/solutions/solutions_demo.m4a', login);
    const video = getVideo('appfiles/solutions/learn_video.mp4', login);
    const dayConfig = learnVideosByDay[module14.currentday];

    if (dayConfig) {
      // Days 1-3: Use pre-configured videos
      setLearnComponent(
        <Learn
          video={getVideo(dayConfig.video, login)}
          songVideo={video}
          closingVideo={getVideo(dayConfig.closingVideo, login)}
          destiny="/solutions"
          song={song}
          back="/solutions"
          background={images.background}
          setDisplayLearn={setDisplayLearn}
          step_completed="MODULE14_SET_stepCompleted"
          module={module14}
        />
      );
    } else {
      // Days 4+: Load user-generated content
      setAnimalVoice(getVideo('appfiles/solutions/solutions_learn_intro.mp3', login));
      setAnimalState(learnIntro);
      setShowAnimal(true);

      try {
        const userSongUrl = await GetUserVideo(`userfiles/${module14.StudentID}/solutions_play_day_music.m4a`);
        const response = await fetch(userSongUrl);
        const influencerVideo = await GetUserVideo(
          `influencers/${influencers[module14.currentday - 4]}/respond_affirmation.mp4`
        );

        const hasUserSong = response.status === 200;

        setLearnComponent(
          <Learn
            songVideo={video}
            destiny="/solutions"
            song={hasUserSong ? userSongUrl : song}
            back="/solutions"
            background={images.background}
            setDisplayLearn={setDisplayLearn}
            step_completed="MODULE14_SET_stepCompleted"
            module={module14}
            instrumental={hasUserSong ? getVideo('appfiles/solutions/solutions_mix.m4a', login) : undefined}
            influencerVideo={influencerVideo}
          />
        );
      } catch (error) {
        console.error('Error loading learn content:', error);
      }
    }

    setDisplayLearn(true);
  };

  const openPractice = () => {
    setAnimalVoice(getVideo('appfiles/solutions/solutions_practice_intro.mp3', login));
    setAnimalState(practiceIntro);
    setShowAnimal(true);
    setDisplayPractice(true);
  };

  const openFTB = () => {
    // Days 4+ show intro animation
    if (module14.currentday > 3) {
      setAnimalVoice(getVideo('appfiles/respond/respond_ftb_intro.mp3', login));
      setAnimalState(ftbIntro);
      setShowAnimal(true);
    }

    setDisplayFTB(true);
  };

  // Celebrate video configuration by day
  const celebrateVideosByDay = {
    1: 'appfiles/solutions/celebrate_day_1.mp4',
    2: 'appfiles/solutions/celebrate_day_2.mp4',
    3: 'appfiles/solutions/celebrate_day_3.mp4',
  };

  const openCelebrate = async () => {
    const celebrateVideo = celebrateVideosByDay[module14.currentday];

    if (celebrateVideo) {
      // Days 1-3: Simple video celebration
      setCelebrateComponent(
        <Celebrate
          module={module14}
          celebrateVideo={getVideo(celebrateVideo, login)}
          background={images.background}
          setDisplayCelebrate={setDisplayCelebrate}
          moduleNum={MODULE_NAME}
        />
      );
    } else {
      // Days 4+: Interactive celebration with recording
      setAnimalVoice(getVideo('appfiles/respond/respond_celebrate_intro.mp3', login));
      setAnimalState(celebrateIntro);
      setShowAnimal(true);

      const voice1 = getVideo('appfiles/respond/respond_celebrate_record_do_all.mp3', login);
      const voice2 = getVideo('appfiles/respond/respond_celebrate_say_yes.mp3', login);
      const voice3 = getVideo('appfiles/respond/respond_amazing_job.mp3', login);
      const animalGIF = getVideo('appfiles/solutions/joyful monkey.gif', login);
      const animalVoiceClip = getVideo('appfiles/solutions/monkey_joyful_jingle_intro.mp3', login);

      try {
        const influencerVideo = await GetUserVideo(
          `influencers/${influencers[module14.currentday - 4]}/respond_affirmation.mp4`
        );

        setCelebrateComponent(
          <Celebrate
            world3={true}
            module={module14}
            background={images.background}
            voice1={voice1}
            voice2={voice2}
            voice3={voice3}
            animalGIF={animalGIF}
            animalVoice={animalVoiceClip}
            text='Record yourself in an excited voice and say, "I can do it all!"'
            name={`solutions_celebration_surprise_${module14.currentday + 1}.mp4`}
            influencerVideo={influencerVideo}
            animation={amazingJob}
            animal={animal}
            setDisplayCelebrate={setDisplayCelebrate}
            moduleNum={MODULE_NAME}
            bigAnimal={true}
          />
        );
      } catch (error) {
        console.error('Error loading celebrate content:', error);
      }
    }

    setDisplayCelebrate(true);
  };

  const displaySinging = () => {
    const singingIntroVideo = getVideo('appfiles/solutions/singing_time.mp4', login);
    const noteVideo = getVideo('appfiles/solutions/solutions_student_notes.mp4', login);
    const singingMusic = getVideo('appfiles/solutions/solutions_demo.m4a', login);
    const instrumental = getVideo('appfiles/solutions/solutions_instrumental.m4a', login);

    setSingTimeComponent(
      <SingingTime
        introVideo={singingIntroVideo}
        demoMusic={singingMusic}
        singingVideo={noteVideo}
        background={images.background}
        singingMusic={singingMusic}
        setDisplaySingingTime={setDisplaySingingTime}
        module={module14}
        moduleNum={MODULE_NAME}
        name="solutions"
        instrumental={instrumental}
        instrumentalURL="appfiles/solutions/solutions_mix.m4a"
      />
    );

    setDisplaySingingTime(true);
  };

  // ------------------------------------------------------------------------
  // Surprise Video Handlers
  // ------------------------------------------------------------------------

  const getSurpriseSlides = async () => {
    const slidePaths = [
      { video: `influencers/${login.influencerid}/respond_affirmation.mp4` },
      { video: `userfiles/${module14.StudentID}/solutions_celebration_surprise_5.mp4` },
      { video: `influencers/${login.influencerid1}/respond_affirmation.mp4` },
      { video: `userfiles/${module14.StudentID}/solutions_celebration_surprise_7.mp4` },
      { video: `influencers/${login.influencerid2}/respond_video_message.mp4` },
      { video: `influencers/${login.influencerid3}/respond_affirmation.mp4` },
      { video: `userfiles/${module14.StudentID}/solutions_celebration_surprise_9.mp4` },
      { video: `influencers/${login.influencerid4}/respond_affirmation.mp4` },
    ];

    // Load all video URLs in parallel
    const loadedSlides = await Promise.all(
      slidePaths.map(async (slide) => ({
        video: await GetUserVideo(slide.video, login),
      }))
    );

    return loadedSlides;
  };

  const playSurpriseVideo = async () => {
    const slides = await getSurpriseSlides();

    setSurpriseVideo(
      <Surprise
        background={images.background}
        music={getVideo('appfiles/solutions/solutions_instrumental.m4a', login)}
        animal={animal}
        back="/"
        slides={slides}
        setDisplaySurprise={setDisplaySurprise}
      />
    );

    setDisplaySurprise(true);
    setKeyIcon(true);
    setPresentIcon(false);
  };

  // ------------------------------------------------------------------------
  // Animation Handlers
  // ------------------------------------------------------------------------

  const coinAnimation = () => {
    setCoinAnimationState('coinAnimation');
    setCoinAudio(true);
  };

  const rewardAnimation = () => {
    setKeyAnimationState('fade-out');
    sleep(1000).then(() => {
      setKeyIcon(false);
      setAnimalVoice(monkeyEatAudio);
      setAnimalState(monkeyEat);
      setShowAnimal(true);
      sleep(3000).then(() => {
        setRewardsFound(true);
        setDisplayReward(true);
      });
    });
  };

  const closeDay9Video = () => {
    setDisplayIntroVideo(true);
    setDisplayBody(false);
    setIntroVideo(getVideo(AFTER_CELEBRATE_VIDEO_DAY9, login));
  };

  // ------------------------------------------------------------------------
  // Drag & Drop Handler
  // ------------------------------------------------------------------------

  const handleDragStop = (rewardId, data) => {
    const position = {
      x: data.x / clientWidth,
      y: data.y / clientHeight,
    };

    const isPrize = rewardId === 1 || rewardId === 2;
    const actionType = isPrize
      ? `MODULE14_SET_PRIZE${rewardId}_POSITION`
      : `MODULE14_SET_REWARD${rewardId}_POSITION`;

    dispatch({ type: actionType, payload: position });
  };

  // ------------------------------------------------------------------------
  // iOS-Specific Timeout Effects
  // ------------------------------------------------------------------------

  // iOS fallback for voice audio
  useEffect(() => {
    if (!displayVoice || !isIOSDevice()) return;

    const timer = setTimeout(() => {
      setAudioReady(false);
      setDisplayVoice(false);
      setShowAnimal(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, [displayVoice]);

  // iOS fallback for animal close audio
  useEffect(() => {
    if (!playAnimalClose || !isIOSDevice()) return;

    const timer = setTimeout(() => {
      setPlayAnimalClose(false);
      if (module14.currentday === SPECIAL_DAYS.DAY_8) {
        closeDay9Video();
      } else {
        navigate(NAVIGATION_PATH);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [playAnimalClose, module14.currentday, navigate]);

  // iOS fallback for coin audio
  useEffect(() => {
    if (!coinAudio || !isIOSDevice()) return;

    const timer = setTimeout(() => {
      dispatch({ type: 'MODULE14_SET_COIN', payload: module14.coins + 1 });
      setCoinAudio(false);
      setCoinAnimationState('bounceIcons');
      setCoinIcon(false);
      playButtonAudio();
    }, 5000);

    return () => clearTimeout(timer);
  }, [coinAudio, dispatch, module14.coins, playButtonAudio]);

  // ------------------------------------------------------------------------
  // Audio Event Handlers
  // ------------------------------------------------------------------------

  const handleCoinAudioEnd = () => {
    dispatch({ type: 'MODULE14_SET_COIN', payload: module14.coins + 1 });
    setCoinAudio(false);
    setCoinAnimationState('bounceIcons');
    setCoinIcon(false);
    playButtonAudio();
  };

  const handleAnimalCloseAudioEnd = () => {
    setPlayAnimalClose(false);
    if (module14.currentday === SPECIAL_DAYS.DAY_8) {
      closeDay9Video();
    } else {
      navigate(NAVIGATION_PATH);
    }
  };

  const handleVoiceAudioPlay = () => {
    setAudioReady(true);
    const stillMonkeyEl = document.getElementById('stillMonkey');
    if (stillMonkeyEl) {
      stillMonkeyEl.style.display = 'none';
    }
  };

  const handleVoiceAudioEnd = () => {
    setAudioReady(false);
    setDisplayVoice(false);
    setAnimalState('');
    setShowAnimal(false);
    setAnimal(images.monkeyStill);
    const stillMonkeyEl = document.getElementById('stillMonkey');
    if (stillMonkeyEl) {
      stillMonkeyEl.style.display = 'block';
    }
  };

  const handleLogout = () => {
    navigate('/');
    userHasAuthenticated(false);
    localStorage.setItem('token', 'false');
  };

  // ------------------------------------------------------------------------
  // Main UI Conditions
  // ------------------------------------------------------------------------

  const shouldShowMainWorld =
    (displayBody &&
      !displayLearn &&
      !displayPractice &&
      !displaySingingTime &&
      !displayCelebrate &&
      !displaySurprise &&
      !displayFTB &&
      !displayDay4Practice) ||
    showAnimal;

  const isSpecialDay = module14.currentday === SPECIAL_DAYS.DAY_4 || module14.currentday === SPECIAL_DAYS.DAY_8;
  const progressBarSource = isSpecialDay ? progressBar5Keys[module14.keys] : progressBar4Keys[module14.keys];
  const progressBarMax = isSpecialDay ? 5 : 4;

  const showActionButtons =
    !showAnimal &&
    !module14.reviewIcon &&
    !coinIcon &&
    !displayPrize &&
    !dropZone;

  const coinTextClass =
    module14.coins < 10
      ? 'scoretext1'
      : module14.coins < COIN_PRIZE_THRESHOLD
      ? 'scoretext10'
      : 'scoretext100';

  // ------------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------------

  return (
    <>
      {/* ========================= Audio Elements ========================= */}
      {coinAudio && (
        <audio autoPlay onEnded={handleCoinAudioEnd}>
          <source src={generateImageUrl('media/Module5Videos/earn_token.mp3')} type="audio/mp3" />
        </audio>
      )}

      {checkSound && (
        <audio autoPlay onEnded={() => setCheckSound(false)}>
          <source src={generateImageUrl('media/Module1Videos/button_accept_item.mp3')} type="audio/mp3" />
        </audio>
      )}

      {playAnimalClose && (
        <audio autoPlay onPlay={() => setAudioReady(true)} onEnded={handleAnimalCloseAudioEnd}>
          <source src={animalCloseAudio} type="audio/mp3" />
        </audio>
      )}

      {rewardsFound && (
        <audio autoPlay>
          <source src={generateImageUrl('media/Module2Videos/daily_rewards_present.mp3')} />
        </audio>
      )}

      {touchButton && (
        <audio autoPlay onEnded={() => setTouchButton(false)}>
          <source src={generateImageUrl('media/Module1Videos/touch_button.mp3')} type="audio/mp3" />
        </audio>
      )}

      {buttonSound && (
        <audio autoPlay onEnded={() => setButtonSound(false)}>
          <source src={generateImageUrl('media/Module1Videos/button_bouncing.mp3')} type="audio/mp3" />
        </audio>
      )}

      {displayVoice && (
        <audio autoPlay onPlay={handleVoiceAudioPlay} onEnded={handleVoiceAudioEnd}>
          <source src={animalVoice} />
        </audio>
      )}

      {/* ========================= Activity Screens ========================= */}
      {displayIntroVideo && renderIntroduction()}

      {!showAnimal && displayLearn && learnComponent}

      {!showAnimal && displayPractice && (
        <Practice
          setDisplayPractice={setDisplayPractice}
          background={images.background}
          stepComplete="MODULE14_SET_stepCompleted"
          module={module14}
          watchConversation={watchConversation}
          specialMessage={specialMessage}
          amazingJob={amazingJob}
          animal={animal}
        />
      )}

      {!showAnimal && displayFTB && (
        <FollowTheBeatModule9
          unit3={true}
          setDisplayFTB={setDisplayFTB}
          awesomeJob={amazingJob}
          instruction={ftbInstruction}
          instructionGIF={ftbInstructionGIF}
          song={ftbMusic}
          count={ftbCount}
          tutorial1={ftbTutorial1}
          tutorial2={ftbTutorial2}
          walkthrough={ftbWalkthrough}
          stepComplete="MODULE14_SET_stepCompleted"
          module={module14}
          score="ftb_solutions"
        />
      )}

      {!showAnimal && displayCelebrate && celebrateComponent}

      {displaySingingTime && singTimeComponent}

      {displaySurprise && surpriseVideo}

      {/* ========================= Main World Content ========================= */}
      {shouldShowMainWorld && (
        <div className="respondWorldBody" style={{ backgroundImage: `url(${images.backgroundWithoutTrees})` }}>
          {/* Tree overlay */}
          <img className="respondWorldTree" src={images.tree} alt="tree" />

          {/* Navigation Bar */}
          <div className="navigation">
            <img
              className="logoutBtn"
              src={images.logoutBtn}
              alt="Logout Button"
              onClick={togglePopup}
            />
            <img
              className="JourneyBlueImage"
              src={images.journeyMap}
              alt="Navigation blue world"
              onClick={() => navigate(NAVIGATION_PATH)}
            />
          </div>

          {/* Logout Popup */}
          {isPopupOpen && (
            <PopUp
              content={
                <>
                  <b id="title">
                    Are you sure you want to leave?
                    <br />
                  </b>
                  <button id="logoutButton1" className="logoutButton" onClick={handleLogout}>
                    YES
                  </button>
                  <button id="logoutButton2" className="logoutButton" onClick={togglePopup}>
                    NO
                  </button>
                </>
              }
              handleClose={togglePopup}
            />
          )}

          {/* Prize Notification */}
          {prize && (
            <>
              <audio autoPlay>
                <source src={presentSound} />
              </audio>
              <img
                className="prize"
                src={generateImageUrl('images/Images/prize.png')}
                alt="Prize"
                onClick={() => {
                  setPrize(false);
                  sleep(2400).then(() => setDisplayPrize(true));
                }}
              />
              <button
                className="prize-button"
                onClick={() => {
                  setPrize(false);
                  setDisplayPrize(true);
                }}
              >
                <h5>Click Here</h5>
              </button>
            </>
          )}

          {/* Prize Selection */}
          {displayPrize && renderPrizeSelection()}

          {/* Monkey Character */}
          <img className="monkey9" id="stillMonkey" src={animal} alt="monkey Icon" />

          {/* Loading indicator for animal animation */}
          {showAnimal && !audioReady && <img alt="Loading" id="loading" src={loadingGif} />}

          {/* Animated Monkey */}
          {showAnimal && (
            <img
              className="monkey9"
              style={audioReady ? {} : { display: 'none' }}
              src={animalState}
              alt="Animated monkey"
              onLoad={() => setDisplayVoice(true)}
              onError={() => setShowAnimal(false)}
            />
          )}

          {/* Score Bar */}
          <div className="scoreBar">
            <img className="scoreKey" src={images.coinBar} alt="coin bar" />
            <h5 className={coinTextClass}>{module14.coins}</h5>
            <p className="scoretext">100</p>
          </div>

          {/* Progress Bar */}
          <div className="progressBar3">
            <img className="progressKey4" src={progressBarSource} alt="progress bar" />
            <h3 className="progresstext3">
              {module14.keys}/{progressBarMax}
            </h3>
          </div>

          {/* Action Buttons (Main Progression) */}
          {showActionButtons && (
            <div className="actionBtnDiv">
              {/* Day 4 starts with singing time */}
              {module14.currentday === SPECIAL_DAYS.DAY_4 && module14.stepCompleted === STEPS.NOT_STARTED && (
                <img
                  className="actionBtn2"
                  id="bounceIcons"
                  src={images.singingTimeBtn}
                  alt="singing time button"
                  onClick={() => {
                    setTouchButton(true);
                    sleep(500).then(() => {
                      displaySinging();
                      setCheckStep(STEPS.SINGING_TIME);
                    });
                  }}
                />
              )}

              {/* Other days start with learn */}
              {module14.currentday !== SPECIAL_DAYS.DAY_4 && module14.stepCompleted === STEPS.NOT_STARTED && (
                <img
                  className="actionBtn2"
                  id="bounceIcons"
                  src={images.learnBtn}
                  alt="learn button"
                  onClick={() => {
                    setTouchButton(true);
                    sleep(500).then(() => {
                      openLearn();
                      setCheckStep(STEPS.LEARN);
                    });
                  }}
                />
              )}

              {/* Practice button after learn */}
              {module14.stepCompleted === STEPS.LEARN && (
                <img
                  className="actionBtn2"
                  id="bounceIcons"
                  src={images.practiceBtn}
                  alt="practice button"
                  onClick={() => {
                    touchSound.play();
                    sleep(500).then(() => {
                      openPractice();
                      setCheckStep(STEPS.PRACTICE);
                    });
                  }}
                />
              )}

              {/* FTB button after practice */}
              {module14.stepCompleted === STEPS.PRACTICE && (
                <img
                  className="actionBtn2"
                  id="bounceIcons"
                  src={images.ftbBtn}
                  alt="follow the beat button"
                  onClick={() => {
                    setTouchButton(true);
                    sleep(500).then(() => {
                      openFTB();
                      setCheckStep(STEPS.FOLLOW_THE_BEAT);
                    });
                  }}
                />
              )}

              {/* Celebrate button after FTB */}
              {module14.stepCompleted === STEPS.FOLLOW_THE_BEAT && (
                <img
                  className="actionBtn2"
                  id="bounceIcons"
                  src={images.celebrateBtn}
                  alt="celebrate button"
                  onClick={() => {
                    setTouchButton(true);
                    sleep(500).then(() => {
                      openCelebrate();
                      setCheckStep(STEPS.CELEBRATE);
                    });
                  }}
                />
              )}

              {/* Learn button after singing time (Day 4 only) */}
              {module14.stepCompleted === STEPS.SINGING_TIME && (
                <img
                  className="actionBtn2"
                  id="bounceIcons"
                  src={images.learnBtn}
                  alt="learn button"
                  onClick={() => {
                    setTouchButton(true);
                    sleep(500).then(() => {
                      openLearn();
                      setCheckStep(STEPS.LEARN);
                    });
                  }}
                />
              )}
            </div>
          )}

          {/* Coin Collection */}
          {!displayVoice && coinIcon && !displayPrize && (
            <div className="actionBtnDiv">
              {!module14.reviewIcon && (
                <img
                  id={coinAnimationState}
                  src={images.coinPick}
                  alt="reward coin"
                  style={{
                    position: 'absolute',
                    top: '50vh',
                    left: '50vw',
                    width: '100px',
                    height: '100px',
                    marginLeft: '-50px',
                    marginTop: '-50px',
                    zIndex: 10,
                  }}
                  onClick={coinAnimation}
                />
              )}
            </div>
          )}

          {/* Review and Reward Buttons */}
          {!displayPrize && !showAnimal && !displayReward && (
            <div className="actionBtnDiv">
              {module14.reviewIcon && (
                <img
                  className={showReward ? 'actionBtn3' : 'actionBtn2'}
                  src={images.reviewBtn}
                  alt="review button"
                  onClick={() => setReviewIconVisible((prev) => !prev)}
                />
              )}
              {showReward && (
                <img
                  src={images.rewardBtn}
                  className="actionBtn4"
                  alt="reward button"
                  onClick={() => {
                    setTouchButton(true);
                    sleep(500).then(() => rewardAnimation());
                  }}
                />
              )}
              {module14.reviewIcon && reviewIconVisible && renderReviewButtons()}
            </div>
          )}

          {/* Banana Reward Icon */}
          {!coinIcon && keyIcon && !displayPrize && !module14.reviewIcon && (
            <img
              id={keyAnimationState}
              className="keyRewards"
              style={{ width: '150px', height: '50px', marginLeft: '-75px', zIndex: 100 }}
              src={images.keyReward}
              alt="reward banana"
              onClick={rewardAnimation}
            />
          )}

          {/* Reward Selection */}
          {displayReward && renderRewardSelection()}

          {/* Present Icon (Day 8) */}
          {!displayPrize && !coinIcon && presentIcon && (
            <>
              <img
                className="present"
                style={{ width: '84px', height: '100px', marginLeft: '-42px', zIndex: 10 }}
                id="bounceIcons"
                src={images.present}
                alt="gift"
                onClick={playSurpriseVideo}
              />
              <audio autoPlay>
                <source src={presentSound} />
              </audio>
            </>
          )}

          {/* Draggable Rewards Area */}
          <div className="rewardArea">
            {module14.rewards.Elephants.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module14.rewards.Elephants.x * clientWidth,
                  y: module14.rewards.Elephants.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(11, data)}
              >
                <div
                  className="moveElephants17"
                  style={{ backgroundImage: `url(${images.rewards.Elephants})`, zIndex: displayGreenButton ? 2 : 0 }}
                />
              </Draggable>
            )}

            {module14.rewards.Zebras.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module14.rewards.Zebras.x * clientWidth,
                  y: module14.rewards.Zebras.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(12, data)}
              >
                <div
                  className="moveReward27"
                  style={{ backgroundImage: `url(${images.rewards.Zebras})`, zIndex: displayGreenButton ? 2 : 0 }}
                />
              </Draggable>
            )}

            {module14.rewards.Yeti.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module14.rewards.Yeti.x * clientWidth,
                  y: module14.rewards.Yeti.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(21, data)}
              >
                <div className="moveYeti1" style={{ backgroundImage: `url(${images.rewards.Yeti})` }} />
              </Draggable>
            )}

            {module14.rewards.YetiBrown.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module14.rewards.YetiBrown.x * clientWidth,
                  y: module14.rewards.YetiBrown.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(22, data)}
              >
                <div className="moveYeti2" style={{ backgroundImage: `url(${images.rewards.YetiBrown})` }} />
              </Draggable>
            )}

            {module14.rewards.JeepBlue.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module14.rewards.JeepBlue.x * clientWidth,
                  y: module14.rewards.JeepBlue.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(31, data)}
              >
                <div
                  className="moveBigReward1"
                  style={{ backgroundImage: `url(${images.rewards.JeepBlue})`, zIndex: displayGreenButton ? 2 : 0 }}
                />
              </Draggable>
            )}

            {module14.rewards.JeepPink.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module14.rewards.JeepPink.x * clientWidth,
                  y: module14.rewards.JeepPink.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(32, data)}
              >
                <div
                  className="moveBigReward2"
                  style={{ backgroundImage: `url(${images.rewards.JeepPink})`, zIndex: displayGreenButton ? 2 : 0 }}
                />
              </Draggable>
            )}

            {module14.rewards.Dragon.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module14.rewards.Dragon.x * clientWidth,
                  y: module14.rewards.Dragon.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(41, data)}
              >
                <div className="moveWidthReward1" style={{ backgroundImage: `url(${images.rewards.Dragon})` }} />
              </Draggable>
            )}

            {module14.rewards.Pheonix.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module14.rewards.Pheonix.x * clientWidth,
                  y: module14.rewards.Pheonix.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(42, data)}
              >
                <div className="moveReward2" style={{ backgroundImage: `url(${images.rewards.Pheonix})` }} />
              </Draggable>
            )}

            {module14.rewards.Tucan.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module14.rewards.Tucan.x * clientWidth,
                  y: module14.rewards.Tucan.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(51, data)}
              >
                <div className="moveSmallReward1" style={{ backgroundImage: `url(${images.rewards.Tucan})` }} />
              </Draggable>
            )}

            {module14.rewards.Parrot.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module14.rewards.Parrot.x * clientWidth,
                  y: module14.rewards.Parrot.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(52, data)}
              >
                <div className="moveSmallReward2" style={{ backgroundImage: `url(${images.rewards.Parrot})` }} />
              </Draggable>
            )}

            {module14.rewards.Horse1.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module14.rewards.Horse1.x * clientWidth,
                  y: module14.rewards.Horse1.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(61, data)}
              >
                <div
                  className="moveReward1"
                  style={{ backgroundImage: `url(${images.rewards.Horse1})`, zIndex: displayGreenButton ? 2 : 0 }}
                />
              </Draggable>
            )}

            {module14.rewards.Horse2.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module14.rewards.Horse2.x * clientWidth,
                  y: module14.rewards.Horse2.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(62, data)}
              >
                <div
                  className="moveReward2"
                  style={{ backgroundImage: `url(${images.rewards.Horse2})`, zIndex: displayGreenButton ? 2 : 0 }}
                />
              </Draggable>
            )}

            {module14.rewards.Lion.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module14.rewards.Lion.x * clientWidth,
                  y: module14.rewards.Lion.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(71, data)}
              >
                <div
                  className="moveAnimal1"
                  style={{ backgroundImage: `url(${images.rewards.Lion})`, zIndex: displayGreenButton ? 2 : 0 }}
                />
              </Draggable>
            )}

            {module14.rewards.Cheetah.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module14.rewards.Cheetah.x * clientWidth,
                  y: module14.rewards.Cheetah.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(72, data)}
              >
                <div
                  className="moveAnimal2"
                  style={{ backgroundImage: `url(${images.rewards.Cheetah})`, zIndex: displayGreenButton ? 2 : 0 }}
                />
              </Draggable>
            )}

            {module14.rewards.Hat1.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module14.rewards.Hat1.x * clientWidth,
                  y: module14.rewards.Hat1.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(81, data)}
              >
                <div className="moveCharaterReward1" style={{ backgroundImage: `url(${images.rewards.Hat1})` }} />
              </Draggable>
            )}

            {module14.rewards.Hat2.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module14.rewards.Hat2.x * clientWidth,
                  y: module14.rewards.Hat2.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(82, data)}
              >
                <div className="moveCharaterReward142" style={{ backgroundImage: `url(${images.rewards.Hat2})` }} />
              </Draggable>
            )}

            {module14.rewards.MedievalCastle.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module14.rewards.MedievalCastle.x * clientWidth,
                  y: module14.rewards.MedievalCastle.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(1, data)}
              >
                <div
                  className="movePrize14"
                  id="behind"
                  style={{ backgroundImage: `url(${images.rewards.MedievalCastle})`, zIndex: displayGreenButton ? 2 : 0 }}
                />
              </Draggable>
            )}

            {module14.rewards.StoneSword.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module14.rewards.StoneSword.x * clientWidth,
                  y: module14.rewards.StoneSword.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(2, data)}
              >
                <div className="moveReward2" style={{ backgroundImage: `url(${images.rewards.StoneSword})` }} />
              </Draggable>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Module14World;
